import {
	Component, Input, Output, EventEmitter, ViewChild, ElementRef, SimpleChanges, ViewContainerRef,
	ChangeDetectionStrategy, ChangeDetectorRef, OnInit, OnChanges, OnDestroy
} from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { FilterSet } from 'app/services/filter-set.service';
import { BbSelect6Component } from '../bb-select-6/bb-select-6.component';
import { OverlayService } from '../../../services/overlay.service';
import { CdkOverlayOrigin, OverlayRef } from '@angular/cdk/overlay';
import { Subject, ReplaySubject, Subscription } from 'rxjs';
import { BbColorPanelComponent } from '../bb-color-panel/bb-color-panel.component';

export interface PillboxConfiguration {
	ctype: string;
	key: string;
	index?: string;
	prefix?: string;
	vtype?: string;
	value?: string;
	suffix?: string;
	dividerLine?: boolean;
	theme?: string;
	showAspectRatioLock?: boolean;
	aspectRatioLocked?: boolean;
	filterSet?: FilterSet;
}
export interface FilterConfigSelect {
	font: boolean;
	type: string | string[];
	entity: string;
	offset: number;
	amount: number;
	sortProperty: string;
	sortDirection: string;
	filterSet?: FilterSet;
}

@Component({
	selector: 'bb-pillbox',
	templateUrl: './bb-pillbox.component.html',
	styleUrls: ['./bb-pillbox.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush
})

export class BbPillboxComponent implements OnInit, OnChanges, OnDestroy {
	@Input() config: PillboxConfiguration;
	@Input() ind: string;
	@Input() isDisabled = false;
	@Output() onChangeProperty: EventEmitter<any> = new EventEmitter<any>();
	@Output() onDropdownOpen: EventEmitter<any> = new EventEmitter<any>();

	@ViewChild('originalEl')
	set setOriginal(originalElRef: ElementRef | undefined) {
		if (originalElRef) {
			this.original = originalElRef.nativeElement as HTMLInputElement;
		}
	}

	@ViewChild('shadowEl')
	set setShadow(shadowElRef: ElementRef | undefined) {
		if (shadowElRef) {
			this.shadow = shadowElRef.nativeElement;
		}
	}

	@ViewChild('colorPicker') colorPicker: CdkOverlayOrigin;

	@ViewChild(BbSelect6Component)
	private selectComponent: BbSelect6Component;


	original: HTMLInputElement;
	shadow: HTMLElement;

	overlayRef: OverlayRef;

	closeColorPickerSubject: ReplaySubject<string>;
	changeColorSubject: ReplaySubject<string>;
	inputChangedColorSubject: ReplaySubject<string>;
	backdropClickSubject: Subject<void>;

	valueOutput: string;
	pillboxControl = new UntypedFormControl();
	filterConfigFont: FilterConfigSelect;
	filterConfigImage: FilterConfigSelect;
	filterConfigMedia: FilterConfigSelect;
	filterConfigApp: FilterConfigSelect;

	private color: string;
	private shadowValue: string;
	private origValue: string;

	private subscriptions: Subscription;

	constructor(
		public viewContainerRef: ViewContainerRef,
		private overlayService: OverlayService,
		private changeDetector: ChangeDetectorRef) {
		this.filterConfigFont = {
			font: true,
			type: 'font',
			entity: 'creative',
			offset: 0,
			amount: 999,
			sortProperty: 'originalfilenameSort',
			sortDirection: 'desc'
		};

		this.filterConfigImage = {
			font: false,
			type: 'image',
			entity: 'commonclip',
			offset: 0,
			amount: 5,
			sortProperty: 'id',
			sortDirection: 'desc',
			filterSet: [{
				filters: [{
					type: 'commonclip',
					field: 'mediatype',
					operator: 'is',
					value: ['image']
				}]
			}]
		};

		this.filterConfigMedia = {
			font: false,
			type: 'media',
			entity: 'commonclip',
			offset: 0,
			amount: 5,
			sortProperty: 'id',
			sortDirection: 'desc',
			filterSet: [{ filters: [] }]
		};

		this.filterConfigApp = {
			font: false,
			type: 'app',
			entity: 'app',
			offset: 0,
			amount: 5,
			sortProperty: 'id',
			sortDirection: 'desc',
			filterSet: [{
				filters: [{
					type: 'app',
					field: 'timelines',
					operator: 'is',
					value: '[1 TO *]'
				}]
			}]
		};

		this.closeColorPickerSubject = new ReplaySubject<string>(1);
		this.changeColorSubject = new ReplaySubject<string>(1);
		this.inputChangedColorSubject = new ReplaySubject<string>(1);
		this.backdropClickSubject = new Subject<void>();

		this.subscriptions = new Subscription();
	}

	ngOnInit() {
		this.update();

		this.subscriptions.add(
			this.changeColorSubject.subscribe((color: string) => {
				this.color = color;
				this.shadowValue = this.color.replace('#', '');
				this.origValue = this.shadowValue;
			})
		);

		this.subscriptions.add(
			this.closeColorPickerSubject.subscribe((color: string) => {
				if (this.config.value !== color.replace('#', '')) {
					this.origValue = color.replace('#', '');
					this.changeProperty(this.config.key, this.origValue);
				}
			})
		);
	}

	ngOnChanges(simpleChanges: SimpleChanges) {
		if (simpleChanges.config && !simpleChanges.config.firstChange || simpleChanges.ind && !simpleChanges.ind.firstChange) {
			if (simpleChanges.config && simpleChanges.config.currentValue.key.endsWith('color')) {
				// Close the color picker(s) on changes so the track selection in the tracks works correctly
				this.closeColorPicker();
			}
			this.update();
		}
	}

	ngOnDestroy() {
		this.closeColorPicker();
	}

	update() {
		this.color = '#' + this.config.value;
		this.origValue = this.config.value;
		this.shadowValue = this.config.value;

		if (this.config.ctype === 'pillbox_multi') {
			this.determineMultiOutput(this.config.value);
		}
		if (this.config.value !== '__...__') {
			this.pillboxControl.setValue(this.config.value);
		} else {
			this.pillboxControl.setValue('');
		}

		if (this.config.ctype === 'pillbox_mediaclip') {
			switch (this.config.index) {
				case '__media__':
					this.filterConfigMedia.filterSet = this.config.filterSet;
					break;

				default:
				case '__image__':
					this.filterConfigImage.filterSet = this.config.filterSet;
					break;

				case '__font__':
					// nothing
					break;
			}
		}

		this.changeDetector.detectChanges();
	}

	determineMultiOutput(value: string) {
		const valueArray = value.split(/[,\s\;]+/, 4);

		if (valueArray.every(v => v === valueArray[0])) {
			this.valueOutput = valueArray[0];
		} else {
			this.valueOutput = valueArray.join(';');
		}
	}

	changeMulti(key: string, val: any) {
		const rawValArray: any[] = val.split(/[,\s\;]+/, 4);
		const valArray = rawValArray.filter(Boolean);
		let newVal = '';

		switch (valArray.length) {
			case 1:
				newVal = `${valArray[0]};${valArray[0]};${valArray[0]};${valArray[0]}`;
				break;

			case 2:
				newVal = `${valArray[0]};${valArray[1]};${valArray[0]};${valArray[1]}`;
				break;

			case 3:
				newVal = `${valArray[0]};${valArray[1]};${valArray[2]};${valArray[2]}`;
				break;

			case 4:
				newVal = valArray.join(';');
				break;
		}
		this.determineMultiOutput(newVal);
		this.changeProperty(key, newVal);
	}

	changeProperty(key: string, val: any, ev: Event = null) {
		if (ev) {
			ev.preventDefault();
		}
		this.onChangeProperty.emit({ key, val });
	}

	changeDropdown(val: any) {
		this.onChangeProperty.emit({ key: this.config.key, val });
	}

	onOriginalChange(value: string) {
		const regex = RegExp(/[a-f0-9]{3}|[a-f0-9]{6}/gmi);
		if (regex.test(value)) {
			this.origValue = value;
			this.shadowValue = this.origValue;
			this.color = '#' + value;
			this.inputChangedColorSubject.next(this.color);
			this.changeProperty(this.config.key, this.origValue);
		}
	}

	onShadowChange(color: string) {
		this.shadowValue = color.substr(1);
		this.color = '#' + this.shadowValue;
		this.origValue = this.shadowValue;
	}

	onCpToggleChange(open: boolean) {
		if (!open) {
			this.original.dispatchEvent(new Event('change'));
		}
	}

	onOriginalFocus(ev: Event) {
		const focusEvent = new Event('click');
		this.shadow.dispatchEvent(focusEvent);
	}

	openColorDropdown() {
		// Select input for color picker
		this.original.select();
		if (this.overlayRef) {
			return;
		}

		this.inputChangedColorSubject.next('#' + this.origValue);

		this.overlayRef = this.overlayService.createConnectedOverlayComponent(
			BbColorPanelComponent,
			this.colorPicker.elementRef,
			this.viewContainerRef,
			{
				inputChangedColorSubject: this.inputChangedColorSubject,
				closeColorPickerSubject: this.closeColorPickerSubject,
				changeColorSubject: this.changeColorSubject,
				backdropClickSubject: this.backdropClickSubject
			},
			[
				{
					function: this.backdropClickSubject.next,
					this: this.backdropClickSubject
				},
				{
					function: () => {
						this.closeColorPicker();
					},
					this: this
				}
			]
		);

		this.changeDetector.reattach();
	}

	closeColorPicker() {
		this.changeDetector.detach();
		this.overlayRef = null;
	}

	openDropdown(ev: Event) {
		this.selectComponent.openPanel(ev);
	}

	dropdownOpened(ev: Event) {
		this.onDropdownOpen.emit(ev);
	}
}
